import { useSnackbar } from "notistack";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useIntervalWhen } from "rooks";

import { useLabelerStatus } from "api/services/useLabelerStatus";
import { CreateLabelValueData } from "api/services/useLabels";
import { useReviewPoolService } from "api/services/useReviewPool";
import { UserStatus } from "config/constants";
import { ImageSettingsContext, ImageSettingsContextType } from "context/ImageSettingsContext/imageSettingsContext";
import { usePostEvent } from "hooks/usePostEvent";
import { HeaderModeChoices } from "pages/review/ReviewPage";
import { Disagreement } from "types/disagreements";
import { DATA_TYPE, Posicheck } from "types/imageQueries";
import { AuthFlow, User } from "types/users";
import {
  constructPollImageQueryUrl,
  filterRecentlyAddedIQs,
  isGroundTruthPool,
  removeDuplicates,
} from "utils/ReviewPageUtils";
import { getTimeDifference } from "utils/utilityFunctions";

import { useCreateLabel } from "./useCreateLabel";

const sound = require("../../assets/notification.mp3");

const REFRESH_MILLISECONDS = 1000;
const INITIAL_STATE = [] as (Posicheck | Disagreement)[];

interface UseReviewPageProps {
  user: User;
}

function useReviewPage(props: UseReviewPageProps) {
  const { user } = props;

  const [searchParams] = useSearchParams();
  const detectorId = searchParams.get("detector_id");
  const groupId = searchParams.get("group_id");
  const isUnclearMode = searchParams.get("unclear_only") === "true";
  const isFlagged = searchParams.get("flagged") === "true";
  const isGroundTruthParam = searchParams.get("ground_truth") === "true";

  const [isLabelerActive, setIsLabelerActive] = useState<boolean>(true);
  const [imageQueriesList, setImageQueriesList] = useState<(Posicheck | Disagreement)[]>(INITIAL_STATE);
  const [labelHistory, setLabelHistory] = useState<(Posicheck | Disagreement)[]>([]);
  const [openNotes, setOpenNotes] = useState<boolean>(false);
  const [openPinnedNotes, setOpenPinnedNotes] = useState<boolean>(true);
  const [isLabeling, setIsLabeling] = useState(false);
  const [undoGracePeriod, setUndoGracePeriod] = useState<number>(0);
  const [lastLabelSubmittedDate, setLastLabelSubmittedDate] = useState<Date>(new Date());
  const [isPooling, setIsPooling] = useState<boolean>(false);
  const [poolAttemptCounter, setPoolAttemptCounter] = useState<number>(0);
  const [seconds, setSeconds] = useState<number>(0);

  const isLoadingImageQueriesRef = useRef(false);
  const shouldProcessPoolDataRef = useRef(true);
  const sendLabelTimerRef = useRef<NodeJS.Timeout | number | undefined>();
  const imageAppearTime = useRef<number>(new Date().getTime());

  const currentImageQuery: Posicheck | Disagreement | null = useMemo(() => {
    if (imageQueriesList.length > 0) {
      imageAppearTime.current = Date.now();
      return imageQueriesList[0];
    }
    return null;
  }, [imageQueriesList]);

  const imageQueryId = useMemo(() => {
    if (currentImageQuery?.data_type === DATA_TYPE.DISAGREEMENT) {
      const disagreement = currentImageQuery as Disagreement;
      return disagreement.iq_id;
    }
    return currentImageQuery?.id || undefined;
  }, [currentImageQuery]);

  const poolFilterMode = useMemo(() => {
    if (isUnclearMode) return HeaderModeChoices.Unclear;
    if (detectorId) return HeaderModeChoices.Detector;
    if (groupId) return HeaderModeChoices.Group;
    return HeaderModeChoices.Global;
  }, [isUnclearMode, detectorId, groupId]);

  const isGroundTruth = isGroundTruthPool(detectorId, isFlagged, isGroundTruthParam);
  const labelIdsHistory = new Set(labelHistory.map((label) => (label as any).id));
  const canUndo = labelHistory.length !== 0;
  const recentElement = canUndo ? labelHistory[labelHistory.length - 1] : undefined;
  const maxSize = isGroundTruth ? 10 : 1;
  const imageQueryPollUrl = constructPollImageQueryUrl(detectorId, groupId, !!isUnclearMode, isFlagged, isGroundTruth);
  const currentDetector = currentImageQuery?.predictor;
  const showIDPMigration = user.auth_flow === AuthFlow.COGNITO && user.enable_idp_migration;
  const shouldStartPooling = isGroundTruth ? imageQueriesList.length < 6 : imageQueriesList.length === 0;

  const { resetImageSettings } = useContext(ImageSettingsContext) as ImageSettingsContextType;

  const { postEvent } = usePostEvent();
  const { enqueueSnackbar } = useSnackbar();

  const handleOpenNotes = () => {
    setOpenNotes(true);
  };

  const handleCloseNotes = () => {
    setOpenNotes(false);
  };

  const handleClosePinnedNotes = () => {
    setOpenPinnedNotes(false);
  };

  const postUndoLabelEvent = useCallback(
    async (timeDiffBetweenLabelAndIgnore: number, mostRecentImageQueryId: string) => {
      try {
        await postEvent("", {
          event_type: "undo_label",
          source: "ReviewLabelQueue",
          undo_time_ms: timeDiffBetweenLabelAndIgnore,
          posicheck_id: mostRecentImageQueryId,
        });
        enqueueSnackbar("Label successfully undone. Changes have been reverted.", {
          variant: "success",
        });
      } catch (err) {
        enqueueSnackbar("Error undoing label. Please try again.", { variant: "error" });
      }
    },
    [enqueueSnackbar, postEvent]
  );

  const clearLabelHistory = useCallback(() => {
    setImageQueriesList(INITIAL_STATE);
    setLabelHistory([]);
  }, []);

  const addToLabelHistory = (recentRemovedIQ: Posicheck | Disagreement) => {
    setLabelHistory((prevLabelHistory: (Posicheck | Disagreement)[]) => {
      // Add the recent removed IQ to the history
      const newHistory = [...prevLabelHistory, recentRemovedIQ];

      if (newHistory.length > maxSize) return newHistory.slice(1);
      return newHistory;
    });
  };

  const removeFromLabelHistory = (recentElement: Posicheck | Disagreement) => {
    setLabelHistory((currentLabelHistory) => currentLabelHistory.slice(0, currentLabelHistory.length - 1));
    setImageQueriesList((prevState) => [recentElement, ...prevState]);
  };

  const removeRecentIQ = (id: string, addToHistory: boolean = true) => {
    const { newPool, removedIQ } = imageQueriesList.reduce(
      (accumulator, imageQuery) => {
        if (imageQuery.id !== id) accumulator.newPool.push(imageQuery);
        else accumulator.removedIQ = imageQuery;
        return accumulator;
      },
      { newPool: [] as (Posicheck | Disagreement)[], removedIQ: null as Posicheck | Disagreement | null }
    );

    // A deleted image query must never be restorable via undo, so callers that
    // remove an IQ for reasons other than labeling (e.g. server-side delete)
    // pass addToHistory=false to keep it out of the undo history.
    if (removedIQ && addToHistory) addToLabelHistory(removedIQ);
    setImageQueriesList(newPool);
    return newPool;
  };

  const stopLabeling = () => {
    setIsLabeling(false);
    restartUndoGracePeriod();
  };

  const undoLabel = () => {
    if (canUndo && recentElement) {
      stopLabeling();
      removeFromLabelHistory(recentElement);
      clearTimeout(sendLabelTimerRef.current);

      const now = new Date();
      const timeDiffBetweenLabelAndIgnore = getTimeDifference(now, lastLabelSubmittedDate);
      postUndoLabelEvent(timeDiffBetweenLabelAndIgnore, (recentElement as any).id);
    }
  };

  const restartSeconds = () => setSeconds(0);

  const restartUndoGracePeriod = () => setUndoGracePeriod(0);

  const restartPoolAttemptCounter = () => setPoolAttemptCounter(0);

  const pool = async () => {
    setSeconds((seconds) => seconds + 1);
    // This will help to avoid queing multiple HTTP request.
    if (isLoadingImageQueriesRef.current) return;
    isLoadingImageQueriesRef.current = true;
    setPoolAttemptCounter((prevCount) => prevCount + 1);
    try {
      await getPoolData();
    } catch (error) {
      console.error(error);
    } finally {
      isLoadingImageQueriesRef.current = false;
    }
  };

  const updateState = (labeledImageQuery: CreateLabelValueData, isDisagreementCheck: boolean) => {
    const now = new Date();
    resetImageSettings();
    setLastLabelSubmittedDate(now);
    restartSeconds();
    if (isDisagreementCheck) {
      removeRecentIQ(labeledImageQuery.posicheck_id);
    }
    setIsLabeling(true);
    restartUndoGracePeriod();
  };

  const playSound = () => {
    const notification = new Audio(sound);
    notification.play();
    restartSeconds();
  };

  const { updateLabelerStatus } = useLabelerStatus();
  const { data: poolData, get: getPoolData } = useReviewPoolService(imageQueryPollUrl, { structuralSharing: false });

  const updateUserStatus = useCallback(
    (statusName: UserStatus) => {
      if (user?.only_labeler_ui) {
        updateLabelerStatus({
          status: statusName,
        });
      }
    },
    [user, updateLabelerStatus]
  );

  const startPooling = useCallback(() => {
    // when start requesting posichecks changes the status to IDLE
    if (user?.only_labeler_ui) {
      updateUserStatus(UserStatus.IDLE);
    }
    setIsPooling(true);
  }, [user, updateUserStatus]);

  const processPoolData = useCallback(async () => {
    if (!poolData || !shouldProcessPoolDataRef.current) return;
    const { results } = poolData;
    const posichecks: Posicheck[] = results;
    const newPoolData = isGroundTruth ? removeDuplicates(imageQueriesList, posichecks) : posichecks;
    const filteredPoolData = filterRecentlyAddedIQs(newPoolData, labelIdsHistory);

    if (filteredPoolData.length > 0) {
      setImageQueriesList(filteredPoolData);
      updateUserStatus(UserStatus.ACTIVE);
      restartPoolAttemptCounter();

      if (seconds > 5) {
        playSound();
      }
      setIsPooling(false);
    } else {
      postEvent("", { event_type: "empty_review_queue", source: "ReviewLabelQueue" });
      setImageQueriesList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolData]);

  const { reviewPageSummary, labelImageQuery } = useCreateLabel({
    imageAppearTime,
    isGroundTruth,
    postEvent,
    sendLabelTimerRef,
    shouldStartPooling,
    startPooling,
    updateState,
    user,
  });

  useEffect(() => {
    processPoolData();
  }, [processPoolData]);

  // This controls when changing the filter view of the review page
  useEffect(() => {
    shouldProcessPoolDataRef.current = false;
    clearLabelHistory();

    setTimeout(() => {
      shouldProcessPoolDataRef.current = true;
    }, 0);
  }, [imageQueryPollUrl, clearLabelHistory]);

  useEffect(() => {
    if (isLabelerActive) {
      restartPoolAttemptCounter();
      startPooling();
    }
  }, [isLabelerActive, startPooling]);

  useEffect(() => {
    if (!isLabelerActive) {
      updateUserStatus(UserStatus.INACTIVE);
      restartPoolAttemptCounter();
      clearLabelHistory();
    }

    const currentTimer = sendLabelTimerRef.current;
    return () => clearTimeout(currentTimer);
  }, [isLabelerActive, clearLabelHistory, updateUserStatus]);

  useIntervalWhen(
    pool,
    REFRESH_MILLISECONDS, // run callback every 1 second
    isPooling && isLabelerActive, // start the timer when it's true
    true // no need to wait for the first interval
  );

  useIntervalWhen(
    () => {
      if (undoGracePeriod >= 3) {
        setIsLabeling(false);
        restartUndoGracePeriod();
      } else {
        setUndoGracePeriod(undoGracePeriod + 1);
      }
    },
    1000,
    isLabeling
  );

  /**
   * Open pinned notes when image query changes.
   */
  useEffect(() => {
    setOpenPinnedNotes(true);
  }, [currentImageQuery?.id]);

  return {
    canUndo,
    currentDetector,
    poolFilterMode,
    imageQueryId,
    currentImageQuery,
    poolAttemptCounter,
    reviewPageSummary,
    showIDPMigration,
    isLabeling,
    openNotes,
    openPinnedNotes,
    isLabelerActive,
    setIsLabelerActive,
    handleOpenNotes,
    handleCloseNotes,
    handleClosePinnedNotes,
    undoLabel,
    removeRecentIQ,
    startPooling,
    labelImageQuery,
  };
}

export default useReviewPage;
